/**
 * Shared programme-schedule builder.
 *
 * Extracts the standalone schedule export's data mappings into a single,
 * presentation-agnostic data model so the same logic can drive both the
 * standalone schedule DOCX export and the descriptor's schedule section.
 *
 * The model intentionally holds display-ready strings/flags (the exact values
 * the schedule cells render) so renderers are a faithful projection of this
 * model with no behavioural drift.
 * @module export/schedule-data
 */

import type { Programme, ProgrammeVersion, Stage } from "@/types";
import { resolveEffectiveAssessments } from "@/utils/assessment-overrides";
import { getAssessmentFlags, getAssessmentPercentages } from "@/utils/assessments";
import {
  exitAwardsByStage,
  exitAwardTitle,
  getProgrammePathwayProjection,
} from "@/utils/award-pathways";
import {
  defaultPatternFor,
  getEffectiveCredits,
  normalizeDeliveryModality,
  resolveEffortHours,
} from "@/utils/helpers";
import {
  formatTeachingPeriod,
  formatTeachingPeriodValue,
  programmeTeachingPeriodLabel,
} from "@/utils/teaching-period";

import { resolveDescriptorVariant } from "./descriptor-variant";

/** Programme delivery-mode ticks (one as appropriate). */
export interface ScheduleDeliveryTicks {
  f2f: boolean;
  blended: boolean;
  online: boolean;
  apprenticeship: boolean;
}

/** Teaching and learning modality ticks (one or more as appropriate). */
export interface ScheduleModalityTicks {
  f2f: boolean;
  syncHybrid: boolean;
  syncOnline: boolean;
  asynchronous: boolean;
  independent: boolean;
  workBased: boolean;
}

/** Assessment techniques used in the stage (one or more as appropriate). */
export interface ScheduleAssessmentTicks {
  continuous: boolean;
  invigilated: boolean;
  proctored: boolean;
  project: boolean;
  practical: boolean;
  workBased: boolean;
}

/** Effort-hour breakdown for a scheduled module row (display strings). */
export interface ScheduleModuleEffort {
  classroomHours: string;
  mentoringHours: string;
  directedElearningHours: string;
  independentLearningHours: string;
  workBasedHours: string;
  /** Other hours that could not be placed in a QQI schedule effort column. */
  otherHours: string;
  /** User-authored explanation for {@link otherHours}. */
  otherHoursSpecify: string;
}

/** Assessment allocation-of-marks for a module row (display strings). */
export interface ScheduleModuleAssessment {
  /** Aggregate continuous-assessment percentage for the numeric schedule cell. */
  continuous: string;
  invigilated: string;
  proctored: string;
  project: string;
  practical: string;
  workBased: string;
}

/** A single module row in the stage schedule table. */
export interface ScheduleModuleRow {
  /**
   * The owning module's code (e.g. "CS801"), so each row still identifies its
   * module after {@link ScheduleData.stages} is flattened into a single row
   * list (e.g. `proposedProgrammeScheduleRows`), matching the 1B.5 Module
   * schedule / 7.1 Module Overview per-row expectations.
   */
  code: string;
  /** The owning programme version's label (e.g. "Full-time"). */
  versionLabel: string;
  /** The owning stage's formatted name (e.g. "Stage 1"). */
  stageName: string;
  /** The owning version's delivery modality (e.g. "F2F"). */
  deliveryModality: string;
  title: string;
  teachingPeriod: string;
  mandatoryOrElective: string;
  credits: string;
  totalHours: string;
  /** Total hours not accounted for by the effort breakdown (total − allocated). */
  unallocatedHours: string;
  /**
   * Per-module export-time warnings surfaced when the module's effort breakdown
   * does not reconcile to its expected total (credits × the credit-hour base).
   * Empty when the effort hours reconcile, so a clean module carries no warning
   * noise (PD-096).
   */
  warnings: string[];
  effort: ScheduleModuleEffort;
  assessment: ScheduleModuleAssessment;
  /**
   * On-site direct contact hours for the FET CAS schedule's four-way effort
   * grouping. The grouping is modality-aware: face-to-face delivery counts
   * classroom, mentoring, and other contact hours as on-site; online delivery
   * counts them as synchronous; blended delivery keeps classroom hours on-site
   * and treats mentoring and other contact as synchronous.
   */
  onsiteContactHours: string;
  /** Synchronous (remote) contact hours under the same modality-aware grouping. */
  synchronousContactHours: string;
  /**
   * Asynchronous non-contact hours: directed e-learning, independent learning,
   * and unclassified other hours.
   */
  asynchronousHours: string;
  /**
   * The CAS component award achievable on this module ("<code> <title>"),
   * resolved from the programme's embedded component-award records; empty when
   * the module is not aligned to a CAS component.
   */
  componentSummary: string;
  /** Count of CAS component awards achievable on this module ("0" or "1"). */
  componentCount: string;
}

/** A single assessment occurring within a populated calendar cell. */
export interface ScheduleCalendarAssessment {
  title: string;
  type: string;
  weighting: number | null;
  outsideTeachingPeriod: boolean;
}

/**
 * A populated calendar cell for a module's weekly schedule: the teaching week
 * plus the assessment(s) scheduled in it, each carrying its title, type, and
 * weighting so the schedule cell shows the assessment detail rather than a bare
 * tick (PD-098). One entry per week that has at least one assessment.
 */
export interface ScheduleCalendarCell {
  week: number;
  assessments: ScheduleCalendarAssessment[];
}

/**
 * An academic-integrity note for a single assessment within a stage.
 *
 * Integrity controls (proctoring, originality checks, AI declarations, …) are
 * surfaced here — in a dedicated per-stage note location — rather than being
 * interleaved into the schedule module rows, so they read as a clear note
 * rather than inline noise in the schedule table (PD-103).
 */
export interface ScheduleIntegrityNote {
  moduleTitle: string;
  assessmentTitle: string;
  /** Human-readable labels of the integrity controls enabled for this assessment. */
  flags: string[];
}

/** A module row within a weekly schedule table. */
export interface ScheduleWeeklyModuleRow {
  title: string;
  activeWeeks: number[];
  /** Populated calendar cells (weeks holding ≥1 assessment), in week order. */
  calendarCells: ScheduleCalendarCell[];
  /** Outside-teaching assessments whose exact week has not been confirmed. */
  unscheduledAssessments: ScheduleCalendarAssessment[];
}

/** A per-teaching-period weekly schedule table. */
export interface ScheduleWeeklyTable {
  stageName: string;
  /** The programme's teaching period label, which {@link teachingPeriodValue} numbers. */
  teachingPeriodLabel: string;
  teachingPeriodValue: string;
  /** Number of scheduled teaching weeks, excluding assessment-only weeks. */
  teachingWeeks: number;
  /** Total calendar columns, extended to the latest assessment week. */
  numWeeks: number;
  modules: ScheduleWeeklyModuleRow[];
}

/** All schedule data for a single (version, stage) pairing. */
export interface ScheduleStageData {
  title: string;
  /**
   * The owning programme version's label (e.g. "Full-time"), so each exported
   * schedule can be labelled with its version and version groups separated
   * (PD-107).
   */
  versionLabel: string;
  /**
   * True only on the first stage emitted for the owning version, letting
   * renderers introduce a version heading / separation before each version's
   * schedules (PD-107).
   */
  isFirstStageOfVersion: boolean;
  providerName: string;
  programmeTitle: string;
  totalCredits: string;
  stageName: string;
  /** The programme's teaching period label, heading the module rows' period column. */
  teachingPeriodLabel: string;
  exitAwardTitle: string;
  stageCredits: string;
  /**
   * Sum of the stage's assigned module credits, counting alternative/excluded
   * elective options only once (options filtered through
   * {@link electiveExclusions} before summing) so duplicate elective pathways
   * don't inflate the total.
   */
  moduleCreditsTotal: string;
  /**
   * True when {@link moduleCreditsTotal} equals {@link stageCredits}, i.e. the
   * learner-taken module credits reconcile to the stage's target credit value.
   */
  creditsReconcile: boolean;
  deliveryTicks: ScheduleDeliveryTicks;
  modalityTicks: ScheduleModalityTicks;
  assessmentTicks: ScheduleAssessmentTicks;
  hasModuleEntries: boolean;
  modules: ScheduleModuleRow[];
  /**
   * Academic-integrity notes for the stage's assessments, collected into a
   * dedicated location rather than interleaved into {@link modules}, so the
   * schedule rows stay free of integrity-flag noise (PD-103). Empty when no
   * assessment in the stage enables an integrity control.
   */
  integrityNotes: ScheduleIntegrityNote[];
  weeklyTables: ScheduleWeeklyTable[];
}

/** The complete schedule data model for a programme. */
export interface ScheduleData {
  stages: ScheduleStageData[];
}

function patternForVersion(version: ProgrammeVersion): ReturnType<typeof defaultPatternFor> {
  const rawModality = version.deliveryModality ?? "";
  const canonicalModality = normalizeDeliveryModality(rawModality);
  const patterns = version.deliveryPatterns ?? {};
  const stored =
    patterns[rawModality] ??
    patterns[canonicalModality] ??
    Object.entries(patterns).find(
      ([key]) => normalizeDeliveryModality(key) === canonicalModality,
    )?.[1];
  return stored ?? defaultPatternFor(canonicalModality);
}

function synchronousOtherHours(effort: Record<string, number>, deliveryModality: string): number {
  const description = String(
    (effort as unknown as Record<string, unknown>).otherHoursSpecify ?? "",
  );
  const isSynchronousDescription =
    /\b(webinars?|live online|synchronous|virtual classroom)\b/i.test(description);
  return normalizeDeliveryModality(deliveryModality) === "ONLINE" && isSynchronousDescription
    ? Number(effort.otherHours ?? 0)
    : 0;
}

/** Human-readable labels for the academic-integrity controls. */
const INTEGRITY_LABELS: Record<string, string> = {
  proctored: "Proctored",
  viva: "Viva/oral",
  inClass: "In-class component",
  originalityCheck: "Originality check",
  aiDeclaration: "AI declaration",
};

/**
 * Render a human-readable label for an integrity-control key, falling back to a
 * de-camelCased form (e.g. "someFlag" → "Some flag") for unknown keys.
 */
function integrityLabel(key: string): string {
  const known = INTEGRITY_LABELS[key];
  if (known) {
    return known;
  }
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Collect the academic-integrity notes for a stage's assessments.
 *
 * One note per assessment that enables at least one integrity control, carrying
 * the module title, assessment title, and the enabled controls' labels. Keeping
 * these in a dedicated per-stage location (rather than in the schedule rows)
 * answers PD-103: integrity flags are surfaced as a separate note rather than
 * interleaved into the schedule table.
 */
function buildIntegrityNotes(
  programme: Programme,
  stageModules: NonNullable<Stage["modules"]>,
): ScheduleIntegrityNote[] {
  const notes: ScheduleIntegrityNote[] = [];

  stageModules.forEach((sm) => {
    const mod = (programme.modules ?? []).find((m) => m.id === sm.moduleId);
    if (!mod) {
      return;
    }

    (mod.assessments ?? []).forEach((a) => {
      const flags = Object.entries(a.integrity ?? {})
        .filter(([, enabled]) => enabled)
        .map(([key]) => integrityLabel(key));
      if (flags.length === 0) {
        return;
      }
      notes.push({
        moduleTitle: mod.title ?? "",
        assessmentTitle: (a.title ?? "").trim() || (a.type ?? "").trim() || "Assessment",
        flags,
      });
    });
  });

  return notes;
}

/**
 * Build the per-module effort-reconciliation warnings for the schedule export.
 *
 * Surfaces a warning when a module's effort breakdown does not reconcile to its
 * expected total (credits × the credit-hour base): either hours remain
 * unallocated or the breakdown over-allocates beyond the total. A reconciling
 * module yields no warning, keeping clean modules free of noise (PD-096).
 */
function buildEffortWarnings(totalHours: number, allocatedHours: number): string[] {
  const unallocated = totalHours - allocatedHours;
  if (unallocated === 0) {
    return [];
  }
  const magnitude = Math.abs(unallocated);
  const descriptor = unallocated > 0 ? "unallocated" : "over-allocated";
  return [
    `Effort hours do not reconcile: ${magnitude} ${descriptor} hour(s) ` +
      `of ${totalHours} total (allocated ${allocatedHours}).`,
  ];
}

/**
 * Learner-effort hours represented by one credit. FET credits carry 10 hours
 * each (a 15-credit CAS component is 150 hours); ECTS carries 25. The HE
 * descriptor's approved hours-per-credit, when authored, overrides both.
 */
function hoursPerCredit(programme: Programme): number {
  return (
    programme.heDescriptor?.approvedHoursPerCredit ??
    (resolveDescriptorVariant(programme) === "cas" ? 10 : 25)
  );
}

function buildModuleRows(
  programme: Programme,
  version: ProgrammeVersion,
  stageName: string,
  stageModules: NonNullable<Stage["modules"]>,
): ScheduleModuleRow[] {
  const deliveryKey = `${version.id}_${version.deliveryModality}`;
  const versionLabel = version.label ?? "";
  const deliveryModality = version.deliveryModality ?? "";
  const creditHourBase = hoursPerCredit(programme);
  const rows: ScheduleModuleRow[] = [];

  stageModules.forEach((sm) => {
    const mod = (programme.modules ?? []).find((m) => m.id === sm.moduleId);
    if (!mod) {
      return;
    }

    const effort = resolveEffortHours(mod, deliveryKey);
    const totalHours = (mod.credits ?? 0) * creditHourBase;
    const reclassifiedSynchronousHours = synchronousOtherHours(effort, deliveryModality);
    const remainingOtherHours = Number(effort.otherHours ?? 0) - reclassifiedSynchronousHours;
    const displayedMentoringHours =
      Number(effort.mentoringHours ?? 0) + reclassifiedSynchronousHours;
    const otherContactHours = Number(effort.otherContactHours ?? 0);
    const allocatedHours =
      (effort.classroomHours ?? 0) +
      displayedMentoringHours +
      otherContactHours +
      (effort.directedElearningHours ?? 0) +
      (effort.independentLearningHours ?? 0) +
      (effort.workBasedHours ?? 0) +
      remainingOtherHours;
    const unallocatedHours = totalHours - allocatedHours;
    const asmPcts = getAssessmentPercentages(mod);

    // Modality-aware four-way grouping for the FET CAS schedule columns.
    const contactHours = (effort.classroomHours ?? 0) + displayedMentoringHours + otherContactHours;
    const canonicalModality = normalizeDeliveryModality(deliveryModality);
    let onsiteContactHours = 0;
    let synchronousContactHours = 0;
    if (canonicalModality === "ONLINE") {
      synchronousContactHours = contactHours;
    } else if (canonicalModality === "BLENDED") {
      onsiteContactHours = effort.classroomHours ?? 0;
      synchronousContactHours = displayedMentoringHours + otherContactHours;
    } else {
      onsiteContactHours = contactHours;
    }
    const asynchronousHours =
      (effort.directedElearningHours ?? 0) +
      (effort.independentLearningHours ?? 0) +
      remainingOtherHours;
    const componentTitle = (programme.embeddedAwards ?? []).find(
      (award) => award.casComponentCode && award.casComponentCode === mod.casComponentCode,
    )?.title;
    const componentSummary = mod.casComponentCode
      ? `${mod.casComponentCode} ${componentTitle ?? mod.title ?? ""}`.trim()
      : "";

    rows.push({
      code: mod.code ?? "",
      versionLabel,
      stageName,
      deliveryModality,
      title: mod.title ?? "",
      teachingPeriod: formatTeachingPeriodValue(sm.semester ?? ""),
      mandatoryOrElective: mod.isElective ? "E" : "M",
      credits: String(mod.credits ?? ""),
      totalHours: String(totalHours),
      unallocatedHours: String(unallocatedHours),
      warnings: buildEffortWarnings(totalHours, allocatedHours),
      effort: {
        classroomHours: String(effort.classroomHours ?? ""),
        mentoringHours: String(displayedMentoringHours || ""),
        directedElearningHours: String(effort.directedElearningHours ?? ""),
        independentLearningHours: String(effort.independentLearningHours ?? ""),
        workBasedHours: String(effort.workBasedHours ?? ""),
        otherHours: String(remainingOtherHours || ""),
        otherHoursSpecify: String(
          (effort as unknown as Record<string, unknown>).otherHoursSpecify ?? "",
        ),
      },
      assessment: {
        continuous: asmPcts.continuous ? String(asmPcts.continuous) : "",
        invigilated: asmPcts.invigilated ? String(asmPcts.invigilated) : "",
        proctored: asmPcts.proctored ? String(asmPcts.proctored) : "",
        project: asmPcts.project ? String(asmPcts.project) : "",
        practical: asmPcts.practical ? String(asmPcts.practical) : "",
        workBased: asmPcts.workBased ? String(asmPcts.workBased) : "",
      },
      onsiteContactHours: onsiteContactHours ? String(onsiteContactHours) : "",
      synchronousContactHours: synchronousContactHours ? String(synchronousContactHours) : "",
      asynchronousHours: asynchronousHours ? String(asynchronousHours) : "",
      componentSummary,
      componentCount: mod.casComponentCode ? "1" : "0",
    });
  });

  return rows;
}

export function representativeStageModules(
  programme: Programme,
  stage: Stage,
): NonNullable<Stage["modules"]> {
  const assignments = stage.modules ?? (stage.moduleIds ?? []).map((moduleId) => ({ moduleId }));
  const groupedElectiveModuleIds = new Set(
    (programme.electiveDefinitions ?? []).flatMap((definition) =>
      (definition.groups ?? []).flatMap((group) => group.moduleIds ?? []),
    ),
  );
  const representativeModuleIds = new Set(
    getProgrammePathwayProjection(programme).selectedModules.map((module) => module.id),
  );

  return assignments.filter(
    (assignment) =>
      !groupedElectiveModuleIds.has(assignment.moduleId) ||
      representativeModuleIds.has(assignment.moduleId),
  );
}

function buildWeeklyTables(
  programme: Programme,
  stage: Stage,
  stageModules: NonNullable<Stage["modules"]>,
): ScheduleWeeklyTable[] {
  const teachingWeeks = stage.teachingWeeks ?? 12;
  const stageName = formatTeachingPeriod(stage.name ?? "");

  const semesterMap = new Map<string, { moduleId: string; semester?: string }[]>();
  stageModules.forEach((sm) => {
    const semester = sm.semester ?? "Unknown";
    if (!semesterMap.has(semester)) {
      semesterMap.set(semester, []);
    }
    semesterMap.get(semester)!.push(sm);
  });

  const tables: ScheduleWeeklyTable[] = [];

  semesterMap.forEach((semesterModules, semester) => {
    const modules: ScheduleWeeklyModuleRow[] = [];
    let latestAssessmentWeek = teachingWeeks;

    semesterModules.forEach((sm) => {
      const mod = (programme.modules ?? []).find((m) => m.id === sm.moduleId);
      if (!mod) {
        return;
      }

      const activeWeeks = new Set<number>();
      const weekAssessments = new Map<number, ScheduleCalendarAssessment[]>();
      const unscheduledAssessments: ScheduleCalendarAssessment[] = [];
      (mod.assessments ?? []).forEach((a) => {
        const calendarAssessment: ScheduleCalendarAssessment = {
          title: a.title ?? "",
          type: a.type ?? "",
          weighting: a.weighting ?? null,
          outsideTeachingPeriod: a.outsideTeachingPeriod === true,
        };
        if (a.outsideTeachingPeriod === true && a.indicativeWeek == null) {
          unscheduledAssessments.push(calendarAssessment);
          return;
        }
        if (a.indicativeWeek && typeof a.indicativeWeek === "number") {
          latestAssessmentWeek = Math.max(latestAssessmentWeek, a.indicativeWeek);
          activeWeeks.add(a.indicativeWeek);
          if (!weekAssessments.has(a.indicativeWeek)) {
            weekAssessments.set(a.indicativeWeek, []);
          }
          weekAssessments.get(a.indicativeWeek)!.push(calendarAssessment);
        }
      });

      const calendarCells: ScheduleCalendarCell[] = Array.from(weekAssessments.entries())
        .map(([week, assessments]) => ({ week, assessments }))
        .sort((x, y) => x.week - y.week);

      modules.push({
        title: mod.title ?? "",
        activeWeeks: Array.from(activeWeeks),
        calendarCells,
        unscheduledAssessments,
      });
    });

    tables.push({
      stageName,
      teachingPeriodLabel: programmeTeachingPeriodLabel(programme),
      teachingPeriodValue: formatTeachingPeriodValue(semester),
      teachingWeeks,
      numWeeks: latestAssessmentWeek,
      modules,
    });
  });

  return tables;
}

function buildStageData(
  programme: Programme,
  version: ProgrammeVersion,
  stage: Stage,
  isFirstStageOfVersion: boolean,
): ScheduleStageData {
  const effectiveProgramme: Programme = {
    ...programme,
    modules: (programme.modules ?? []).map((module) => ({
      ...module,
      assessments: resolveEffectiveAssessments(module, version).assessments,
    })),
  };
  const stageModules = representativeStageModules(programme, stage);
  const dm = normalizeDeliveryModality(version.deliveryModality);
  const stageName = formatTeachingPeriod(stage.name ?? "");
  const assessmentFlags = getAssessmentFlags(effectiveProgramme, stageModules);
  const pattern = patternForVersion(version);
  const hasAsyncDirected = Number(pattern?.asyncDirectedPct ?? 0) > 0;
  const exitAward = exitAwardTitle(exitAwardsByStage(programme, version).get(stage.id));

  const moduleCreditsSum = getEffectiveCredits(
    programme,
    stageModules.map((entry) => entry.moduleId),
  ).total;
  const stageCredits = String(stage.creditsTarget ?? "");
  const moduleCreditsTotal = String(moduleCreditsSum);

  const moduleRows = buildModuleRows(effectiveProgramme, version, stageName, stageModules);
  const hasEffort = (field: keyof ScheduleModuleEffort): boolean =>
    moduleRows.some((row) => Number(row.effort[field] ?? 0) > 0);

  return {
    title: `Proposed Programme Schedule(s) - ${version.label ?? ""} / ${stageName}`,
    versionLabel: version.label ?? "",
    isFirstStageOfVersion,
    providerName: programme.providerSettings?.name ?? "",
    programmeTitle: programme.title ?? "",
    totalCredits: String(programme.totalCredits ?? ""),
    stageName,
    teachingPeriodLabel: programmeTeachingPeriodLabel(programme),
    exitAwardTitle: exitAward,
    stageCredits,
    moduleCreditsTotal,
    creditsReconcile: moduleCreditsTotal === stageCredits,
    deliveryTicks: {
      f2f: dm === "F2F",
      blended: dm === "BLENDED",
      online: dm === "ONLINE",
      apprenticeship: dm === "APPRENTICESHIP",
    },
    modalityTicks: {
      f2f: dm === "F2F",
      syncHybrid: dm === "BLENDED",
      syncOnline: dm === "ONLINE",
      asynchronous: hasAsyncDirected,
      independent: hasEffort("independentLearningHours"),
      workBased: hasEffort("workBasedHours"),
    },
    assessmentTicks: {
      continuous: assessmentFlags.continuous,
      invigilated: assessmentFlags.invigilated,
      proctored: assessmentFlags.proctored,
      project: assessmentFlags.project,
      practical: assessmentFlags.practical,
      workBased: assessmentFlags.workBased,
    },
    hasModuleEntries: stageModules.length > 0,
    modules: moduleRows,
    integrityNotes: buildIntegrityNotes(effectiveProgramme, stageModules),
    weeklyTables: buildWeeklyTables(effectiveProgramme, stage, stageModules),
  };
}

/**
 * Builds the presentation-agnostic schedule data model for a programme,
 * flattening every (version, stage) pairing into ordered stage entries.
 */
export function buildScheduleData(programme: Programme): ScheduleData {
  const stages: ScheduleStageData[] = [];

  (programme.versions ?? []).forEach((version) => {
    (version.stages ?? []).forEach((stage, stageIndex) => {
      stages.push(buildStageData(programme, version, stage, stageIndex === 0));
    });
  });

  return { stages };
}
